if (!window.ZenitScrollLock) {
  window.ZenitScrollLock = {
    owners: new Set(),
    scrollPosition: 0,
    previousStyles: null,
    hadOverflowHidden: false,

    lock(owner) {
      if (!owner || this.owners.has(owner)) return;

      this.owners.add(owner);
      if (this.owners.size > 1) return;

      const body = document.body;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      const currentPaddingRight = parseFloat(window.getComputedStyle(body).paddingRight) || 0;

      this.scrollPosition = window.scrollY;
      this.hadOverflowHidden = body.classList.contains('overflow-hidden');
      this.previousStyles = {
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        width: body.style.width,
        paddingRight: body.style.paddingRight,
      };
      if (scrollbarWidth > 0) body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
      body.style.position = 'fixed';
      body.style.top = `-${this.scrollPosition}px`;
      body.style.left = '0';
      body.style.width = '100%';
      body.classList.add('overflow-hidden', 'zenit-scroll-locked');
    },

    unlock(owner) {
      if (!owner || !this.owners.delete(owner) || this.owners.size > 0) return;

      const body = document.body;
      const previousStyles = this.previousStyles || {};

      body.style.position = previousStyles.position || '';
      body.style.top = previousStyles.top || '';
      body.style.left = previousStyles.left || '';
      body.style.width = previousStyles.width || '';
      body.style.paddingRight = previousStyles.paddingRight || '';
      body.classList.remove('zenit-scroll-locked');
      if (!this.hadOverflowHidden) body.classList.remove('overflow-hidden');
      window.scrollTo(0, this.scrollPosition);
      this.previousStyles = null;
    },
  };
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.onKeyUp = this.handleKeyUp.bind(this);
    this.onDrawerClick = this.handleDrawerClick.bind(this);
    this.onCartLinkClick = this.handleCartLinkClick.bind(this);
    this.onCartLinkKeyDown = this.handleCartLinkKeyDown.bind(this);
    this.backgroundElements = [];

    // Capture Escape before Dawn's document-level details handler so closing an
    // open cart disclosure never also closes the drawer.
    document.addEventListener('keyup', this.onKeyUp, true);
    this.addEventListener('click', this.onDrawerClick);
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    if (this.openFrame) cancelAnimationFrame(this.openFrame);
    if (this.focusFrame) cancelAnimationFrame(this.focusFrame);
    this.cartLink?.removeEventListener('click', this.onCartLinkClick);
    this.cartLink?.removeEventListener('keydown', this.onCartLinkKeyDown);
    document.removeEventListener('keyup', this.onKeyUp, true);
    this.classList.remove('active');
    this.unlockBackground();
    window.ZenitScrollLock.unlock(this);
    this.cartLink?.setAttribute('aria-expanded', 'false');
    if (this.getAttribute('aria-hidden') === 'false') {
      const returnFocusTarget = this.activeElement?.isConnected ? this.activeElement : this.cartLink;
      removeTrapFocus(returnFocusTarget?.isConnected ? returnFocusTarget : null);
    }
    this.inert = true;
    this.setAttribute('aria-hidden', 'true');
    this.activeElement = null;
  }

  setHeaderCartIconAccessibility() {
    const cartBubble = document.querySelector('#cart-icon-bubble');
    const cartLink = cartBubble?.closest('a') || cartBubble;
    if (!cartLink) return;

    this.cartLink = cartLink;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    cartLink.addEventListener('click', this.onCartLinkClick);
    cartLink.addEventListener('keydown', this.onCartLinkKeyDown);
  }

  handleCartLinkClick(event) {
    event.preventDefault();
    this.open(this.cartLink);
  }

  handleCartLinkKeyDown(event) {
    if (event.code.toUpperCase() !== 'SPACE') return;

    event.preventDefault();
    this.open(this.cartLink);
  }

  handleKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE' || this.getAttribute('aria-hidden') !== 'false') return;
    if (document.querySelector('cart-disclosure-modal[open]')) return;

    const expandedQuantityButton = this.querySelector('quantity-popover [aria-expanded="true"]');
    if (expandedQuantityButton) {
      event.preventDefault();
      event.stopPropagation();
      const quantityPopover = expandedQuantityButton.closest('quantity-popover');
      quantityPopover?.closePopover(event);
      expandedQuantityButton.focus();
      return;
    }

    event.preventDefault();
    const openDetails = event.target.closest('details[open]');
    if (openDetails && this.contains(openDetails)) {
      event.stopPropagation();
      openDetails.removeAttribute('open');
      const summary = openDetails.querySelector('summary');
      summary?.setAttribute('aria-expanded', 'false');
      summary?.focus();
      return;
    }

    this.close();
  }

  handleDrawerClick(event) {
    const closeTrigger = event.target.closest('[data-cart-drawer-close], #CartDrawer-Overlay');
    if (!closeTrigger || !this.contains(closeTrigger)) return;

    event.preventDefault();
    this.close();
  }

  open(triggeredBy) {
    if (this.getAttribute('aria-hidden') === 'false') return;
    const currentFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const returnFocusTarget = triggeredBy || (!this.contains(currentFocus) ? currentFocus : null) || this.cartLink;
    if (returnFocusTarget) this.setActiveElement(returnFocusTarget);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.inert = false;
    this.setAttribute('aria-hidden', 'false');
    this.lockBackground();
    window.ZenitScrollLock.lock(this);
    this.cartLink?.setAttribute('aria-expanded', 'true');

    this.classList.add('animate', 'active');
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : this.querySelector('.drawer__inner');
    const focusElement = containerToTrapFocusOn?.querySelector('.drawer__close') || containerToTrapFocusOn;
    if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    if (this.getAttribute('aria-hidden') !== 'false') return;
    if (this.openFrame) {
      cancelAnimationFrame(this.openFrame);
      this.openFrame = null;
    }
    if (this.focusFrame) {
      cancelAnimationFrame(this.focusFrame);
      this.focusFrame = null;
    }

    document.querySelectorAll('cart-disclosure-modal[open]').forEach((modal) => {
      if (modal.opener && this.contains(modal.opener)) modal.hide(false);
    });

    const returnFocusTarget = this.activeElement?.isConnected ? this.activeElement : this.cartLink;
    this.classList.remove('active');
    this.cartLink?.setAttribute('aria-expanded', 'false');
    this.unlockBackground();
    window.ZenitScrollLock.unlock(this);
    removeTrapFocus(returnFocusTarget);
    this.inert = true;
    this.setAttribute('aria-hidden', 'true');
    this.activeElement = null;
  }

  lockBackground() {
    this.backgroundElements = Array.from(document.body.children).filter(
      (element) =>
        element !== this &&
        element.tagName !== 'CART-DISCLOSURE-MODAL' &&
        !['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(element.tagName) &&
        !element.inert
    );
    this.backgroundElements.forEach((element) => {
      element.inert = true;
    });
  }

  unlockBackground() {
    this.backgroundElements.forEach((element) => {
      element.inert = false;
    });
    this.backgroundElements = [];
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() !== 'ESCAPE' || !event.currentTarget.hasAttribute('open')) return;
      event.stopPropagation();
      onKeyUpEscape(event);
    });
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    try {
      this.renderSections(parsedState.sections);
      this.open();
    } catch (renderError) {
      this.refresh()
        .then(() => this.open())
        .catch((refreshError) => {
          console.error('[Cart drawer] Unable to render the updated cart.', renderError, refreshError);
          const errorMessage = window.cartStrings?.error || '';
          const errorRegion = document.getElementById('CartDrawer-CartErrors');
          if (errorRegion) errorRegion.textContent = errorMessage;
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cart-drawer',
            errors: errorMessage,
            message: errorMessage,
          });
        });
    }
  }

  renderSections(sections) {
    const drawerHTML = sections?.['cart-drawer'];
    if (!drawerHTML) throw new Error('The cart drawer section was not returned.');

    const drawerDocument = new DOMParser().parseFromString(drawerHTML, 'text/html');
    const sourceDrawer = drawerDocument.querySelector('cart-drawer');
    const sourceContents = sourceDrawer?.querySelector('#CartDrawer');
    const targetContents = this.querySelector('#CartDrawer');
    if (!sourceDrawer || !sourceContents || !targetContents) throw new Error('The cart drawer section is invalid.');

    const focusedBundleButton = this.contains(document.activeElement)
      ? document.activeElement.closest('[data-cart-bundle-add]')
      : null;
    const focusedBundleVariantId = focusedBundleButton?.dataset.cartBundleVariantId;

    targetContents.innerHTML = sourceContents.innerHTML;
    this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    if (focusedBundleVariantId && this.classList.contains('active')) {
      if (this.focusFrame) cancelAnimationFrame(this.focusFrame);
      this.focusFrame = requestAnimationFrame(() => {
        const refreshedBundleButton = Array.from(this.querySelectorAll('[data-cart-bundle-variant-id]')).find(
          (button) => button.dataset.cartBundleVariantId === focusedBundleVariantId
        );
        const focusTarget = refreshedBundleButton || this.querySelector('#CartDrawer-Checkout, .drawer__close');
        focusTarget?.focus();
        this.focusFrame = null;
      });
    }

    const cartIconHTML = sections?.['cart-icon-bubble'];
    const cartIconTarget = document.getElementById('cart-icon-bubble');
    if (cartIconHTML && cartIconTarget) {
      const cartIconDocument = new DOMParser().parseFromString(cartIconHTML, 'text/html');
      const cartIconSource = cartIconDocument.querySelector('.shopify-section');
      if (cartIconSource) cartIconTarget.innerHTML = cartIconSource.innerHTML;
    }
  }

  async refresh() {
    const focusedElement = this.contains(document.activeElement) ? document.activeElement : null;
    const focusedLineKey = focusedElement?.closest('.cart-item')?.querySelector('[data-quantity-line-key]')?.dataset
      .quantityLineKey;
    const focusedName = focusedElement?.getAttribute('name');
    const response = await fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`);
    if (!response.ok) throw new Error(`Cart refresh failed with status ${response.status}`);

    const responsePayload = await response.json();
    const sections = responsePayload.sections || responsePayload;
    this.renderSections(sections);

    if (this.classList.contains('active')) {
      const refreshedInput = focusedLineKey
        ? Array.from(this.querySelectorAll('[data-quantity-line-key]')).find(
            (input) => input.dataset.quantityLineKey === focusedLineKey
          )
        : null;
      const refreshedLine = refreshedInput?.closest('.cart-item');
      const focusTarget =
        (focusedName && refreshedLine?.querySelector(`[name="${focusedName}"]`)) ||
        refreshedInput ||
        this.querySelector('.drawer__close, .drawer__inner');
      const focusContainer = this.querySelector('#CartDrawer');
      if (focusContainer && focusTarget) trapFocus(focusContainer, focusTarget);
    }

    return sections;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = html ? new DOMParser().parseFromString(html, 'text/html').querySelector(selector) : null;
    return element?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
